import { Box, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	DownloadExcelTemplate,
	ErrorMessage,
	FormDropdownFilter,
} from "components";
import { FlexNetPartNumberWarning } from "components/FlexNetPartNumberWarning";
import { Notice } from "components/Notice";
import { UnsupportedFlexNetPlant } from "lib/flexnet/partNumberGate";
import { Control } from "react-hook-form";
import * as constants from "lib/util";
import { ContainmentTypeConfig } from "../hooks/containmentTypeConfig";

import { Sector } from "models";
import * as labels from "./constants";
import { CardShell, SectorDisplay, PlantDisplay } from "./shared";
import { SectorsAndPlantsReadonly } from "./SectorsAndPlantsReadonly";
import { PlantsDropdown } from "./PlantsDropdown";

interface sectorsAndPlantsCardProps {
	readonly?: boolean;
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	control?: Control<any>;
	sectors?: SectorDisplay[];
	plants?: PlantDisplay[];
	typeConfig?: ContainmentTypeConfig;
	hasSectorSelected?: boolean;
	/** True when at least one impacted plant is selected (required to show the FlexNet template download). */
	hasPlantSelected?: boolean;
	sectorsDropdownKey?: string;
	sectorFilterType?: any;
	plantFilterType?: any;
	availablePlants?: any[];
	containmentTypeApiName?: string;
	watchedSectors?: any[];
	getSectorOptionDisabled?: (sector: any) => boolean;
	getImpactedPlantOptionDisabled?: (plant: any) => boolean;
	handleSectorDataLoaded?: (sectors: Sector[]) => void;
	handleSectorChange?: (args: { value: any }) => void;
	onPlantChange?: (args: { field: any; value: any }) => void;
	plantValidationRules?: any;
	unauthorizedPlantNames?: string[];
	/** Plants below the FlexNet support threshold; warning renders under the template download info. */
	unsupportedPlants?: UnsupportedFlexNetPlant[];
	/**
	 * FlexNet PN gate: selects Part|Serial vs Serial-only download template.
	 * Same validation as {@link FlexNetPartNumberWarning} (gate fails → with Part).
	 * When plants are not yet selected, pass true (fail closed / with-PN template).
	 */
	partNumberRequired?: boolean;
}

export function SectorsAndPlantsCard({
	readonly = false,
	control,
	sectors = [],
	plants = [],
	typeConfig,
	hasSectorSelected = false,
	hasPlantSelected = false,
	sectorsDropdownKey,
	sectorFilterType,
	plantFilterType,
	availablePlants = [],
	containmentTypeApiName,
	watchedSectors = [],
	getSectorOptionDisabled,
	getImpactedPlantOptionDisabled,
	handleSectorDataLoaded,
	handleSectorChange,
	onPlantChange,
	plantValidationRules,
	unauthorizedPlantNames = [],
	unsupportedPlants = [],
	partNumberRequired = true,
}: sectorsAndPlantsCardProps): React.JSX.Element {
	if (readonly && sectors.length === 0 && plants.length === 0) return null;

	const plantCount = plants.length;
	const readonlyLabel = readonly ? (
		<>
			{labels.ImpactedSectorsAndPlants}{" "}
			<span style={{ display: "inline-block" }}>({plantCount} {plantCount === 1 ? "plant" : "plants"})</span>
		</>
	) : labels.ImpactedSectorsAndPlants;

	const showDownloadTemplate =
		Boolean(typeConfig?.hasHolds) && hasSectorSelected && hasPlantSelected;
	const showFlexNetPartNumberWarning = unsupportedPlants.length > 0;

	return (
		<CardShell readonly={readonly} label={readonlyLabel} toolTip="Select impacted sectors and plants" gap={2.5}>
			{readonly ? (
				<SectorsAndPlantsReadonly sectors={sectors} plants={plants} />
			) : (
				<>
					<Box data-test-selector="sector-dropdown">
						<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
							{labels.Sectors}
						</Typography>
						<FormDropdownFilter
							key={sectorsDropdownKey}
							control={control!}
							name="sectors"
							multiple
							autoTrigger
							disabled={typeConfig?.sectorAutoSelected}
							filterType={sectorFilterType}
							getOptionDisabled={getSectorOptionDisabled}
							getOptionClassName={(sector: any) =>
								sector.name?.toUpperCase() === constants.RESS
									? "sector-ress-divider"
									: undefined
							}
							onDataLoaded={handleSectorDataLoaded}
							onChange={handleSectorChange}
							keyFunc={(sector: any) => sector.id}
							labelFunc={(sector: any) => sector.name}
							rules={{
								required: "At least one sector must be selected",
								validate: (s: any) => {
									if (!Array.isArray(s) || s.length === 0)
										return "At least one sector must be selected";
									return true;
								},
							}}
							renderError={(err: any) =>
								err.IfErr((msg: string) => <ErrorMessage msg={msg} />).UnwrapOrNil()
							}
						/>
					</Box>

					{typeConfig?.autoSelectAllPlants && hasSectorSelected && (
						<>
							<Notice
								message="All plants with this feature enabled will be included in this ASRS containment."
								delay={0}
							/>
							{unauthorizedPlantNames.length > 0 && (
								<Notice
									message={`You do not have Lock permission for: ${unauthorizedPlantNames.join(", ")}. Reach out to sector administrators or plant user access managers to request access.`}
									delay={0}
									icon={
										<WarningAmberIcon
											sx={{ color: "error.main", mr: 1, fontSize: 18 }}
										/>
									}
								/>
							)}
						</>
					)}

					{hasSectorSelected && !typeConfig?.autoSelectAllPlants && (
						<PlantsDropdown
							control={control!}
							containmentTypeApiName={containmentTypeApiName}
							watchedSectors={watchedSectors}
							plantFilterType={plantFilterType}
							availablePlants={availablePlants}
							getImpactedPlantOptionDisabled={getImpactedPlantOptionDisabled}
							onPlantChange={onPlantChange}
							plantValidationRules={plantValidationRules}
						/>
					)}

					{(showDownloadTemplate || showFlexNetPartNumberWarning) && (
						<Box display="flex" flexDirection="column" gap={2}>
							{showDownloadTemplate && (
								<DownloadExcelTemplate
									selectedSector={
										watchedSectors.find((s: any) => s.name === constants.RESS) ||
										watchedSectors[0]
									}
									partNumberRequired={partNumberRequired}
								/>
							)}
							{showDownloadTemplate && (
								<Notice
									message={
										(watchedSectors.some((s: any) => s.name === constants.RESS)
											? "Serial Number should be in the (DUNS-Trace) format for accurate processing."
											: "Serial Number should be in the (Trace) format for accurate processing.") +
										(watchedSectors.length > 1
											? " This containment will be created for multiple GPS sectors."
											: "")
									}
									delay={0}
								/>
							)}
							{showFlexNetPartNumberWarning && (
								<FlexNetPartNumberWarning unsupportedPlants={unsupportedPlants} />
							)}
						</Box>
					)}
				</>
			)}
		</CardShell>
	);
}
